import type Matrix from "./matrix";
import Vector from "./vector";

const rowBudgetCache = new WeakMap<
  Matrix<"tranche", "evaluable+hold">,
  Float64Array
>();

function rowBudgetSums(
  tranches: Matrix<"tranche", "evaluable+hold">,
): Float64Array {
  const cached = rowBudgetCache.get(tranches);
  if (cached != null) {
    return cached;
  }

  const [numRows, numEvaluables] = tranches.shape;
  const sums = new Float64Array(numRows);
  const data = tranches.data;

  for (let rowNo = 0; rowNo < numRows; rowNo++) {
    const rowOffset = rowNo * numEvaluables;
    let sum = 0;
    for (let colNo = 0; colNo < numEvaluables; colNo++) {
      sum += data[rowOffset + colNo]!;
    }
    sums[rowNo] = sum;
  }

  rowBudgetCache.set(tranches, sums);
  return sums;
}

export function unilateralAlloc(
  tranches: Matrix<"tranche", "evaluable+hold">,
  budget: number,
): Vector<"evaluable+hold"> {
  const [, numEvaluables] = tranches.shape;
  const out = new Float64Array(numEvaluables);
  unilateralAllocInto(tranches, budget, out);
  return new Vector(out);
}

// INVARIANT: `tranches` must be a cumulative tranche table, so its row sums
// are nondecreasing — firstRowUsingWholeBudget binary-searches on that basis.
// Every producer upholds this: trancheListToAlgoTranches and
// buildMatchingPreferenceMatrix accumulate nonnegative marginal amounts into
// running per-column totals, and getFunderTrancheProfile applies a funder's
// negative steps as LIFO reductions against earlier tranches specifically to
// keep the table monotone. A non-monotone table here is a bug in the producer.
export function unilateralAllocInto(
  tranches: Matrix<"tranche", "evaluable+hold">,
  budget: number,
  out: Float64Array,
  outOffset = 0,
): number {
  const [numRows, numEvaluables] = tranches.shape;
  if (budget === 0) {
    for (let colNo = 0; colNo < numEvaluables; colNo++) {
      out[outOffset + colNo] = 0;
    }
    return 0;
  }

  const rowSums = rowBudgetSums(tranches);
  const i = firstRowUsingWholeBudget(rowSums, budget);
  const tranchesData = tranches.data;

  // TODO: this happens when the tranche list absorbs the whole budget. When/if
  // that happens should the rest of the budget be sent to hold? (which, by
  // convention, should be the first column)
  if (i === -1) {
    const rowStart = (numRows - 1) * numEvaluables;
    let rowSum = 0;
    for (let colNo = 0; colNo < numEvaluables; colNo++) {
      const value = tranchesData[rowStart + colNo]!;
      out[outOffset + colNo] = value;
      rowSum += value;
    }
    return rowSum;
  }

  const rowStart = i * numEvaluables;
  const previousRowStart = (i - 1) * numEvaluables;
  const fullyUsedBudget = i > 0 ? rowSums[i - 1]! : 0;
  const marginalTotal = rowSums[i]! - fullyUsedBudget;
  const scale = (budget - fullyUsedBudget) / marginalTotal;

  let rowSum = 0;
  for (let colNo = 0; colNo < numEvaluables; colNo++) {
    const fullyUsed = i > 0 ? tranchesData[previousRowStart + colNo]! : 0;
    const marginal = tranchesData[rowStart + colNo]! - fullyUsed;
    const value = fullyUsed + marginal * scale;
    out[outOffset + colNo] = value;
    rowSum += value;
  }

  return rowSum;
}

// Lower bound: first index with rowSums[i] >= budget. Relies on rowSums being
// nondecreasing (cumulative tranche tables — see the invariant note on
// unilateralAllocInto). This lookup runs once per evaluator per S-Process
// epoch over tables with potentially thousands of tranches, so the log-time
// search matters.
function firstRowUsingWholeBudget(
  rowSums: Float64Array,
  budget: number,
): number {
  let lo = 0;
  let hi = rowSums.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (rowSums[mid]! >= budget) {
      hi = mid;
    } else {
      lo = mid + 1;
    }
  }
  return lo < rowSums.length ? lo : -1;
}
